// Chapter 2: Crop Hail Insurance
// Complete study material from SD Crop Insurance License Exam Manual

const chapter2 = {
  id: 2,
  title: "Crop Hail Insurance",
  sections: [
    {
      title: "Introduction to Crop Hail Insurance",
      content: `
        <h2>Introduction to Crop Hail Insurance</h2>
        <p><strong>Crop-Hail Insurance (CH)</strong> is a unique coverage designed to indemnify farmers and other crop growers for loss of yield as a direct result of hail damage to growing crops.</p>

        <div class="key-concept">
          <strong>Key Characteristics of Crop-Hail Insurance:</strong>
          <ul>
            <li>Crop-Hail policies are <strong>NOT part of the Federal Crop Insurance Program</strong></li>
            <li>They are provided <strong>directly to farmers by private insurers</strong></li>
            <li>Since it is a type of private insurance, it should <strong>not be confused with Multiple Peril Crop Insurance (MPCI)</strong>, which is federally subsidized</li>
            <li>CH policy rules and rates can <strong>vary with provider</strong></li>
            <li>Unlike MPCI, a CH policy can be <strong>purchased at any time during the growing season</strong></li>
          </ul>
        </div>

        <h3>What Does Crop-Hail Insurance Cover?</h3>
        <p>The crop-hail policy is specialized because it protects the insured against loss to planted crops resulting from <strong>hail, fire, and lightning</strong>. The crop is covered:</p>
        <ul>
          <li>While growing prior to harvest</li>
          <li>Against fire damage until harvested and stored</li>
        </ul>

        <h3>Coverage Flexibility</h3>
        <p>Crop-hail insurance can be written in conjunction with a MPCI policy or as a <strong>stand-alone coverage</strong>. It can be purchased in any amount up to the <strong>full value of the expected crop</strong>.</p>

        <div class="definition">
          <strong>Definition:</strong> The crop-hail policy provides an <strong>acre-by-acre protection</strong> that can be up to the actual cash value of the crop.
        </div>

        <div class="exam-tip">
          <strong>Exam Tip:</strong> Remember that Crop-Hail insurance is PRIVATE insurance (not federal), can be purchased ANYTIME during growing season, and covers hail, fire, and lightning.
        </div>
      `
    },
    {
      title: "Eligibility and Insureds",
      content: `
        <h2>Eligibility and Insureds</h2>

        <h3>Insurable Interest Requirement</h3>
        <p>Crop insurance is similar to other forms of insurance because it requires an <strong>insurable interest</strong> on the part of the insured. This means that in order to be eligible for crop insurance, a person must have an <strong>economic interest in the growing crop</strong>.</p>

        <div class="key-concept">
          <strong>Who Can Be Insured?</strong>
          <ul>
            <li><strong>Landowners</strong> - Own the land and have interest in crops grown</li>
            <li><strong>Tenant farmers</strong> - Rent land and grow crops</li>
            <li><strong>Sharecroppers</strong> - Work land for a share of the crop</li>
          </ul>
        </div>

        <h3>Banks and Lending Institutions</h3>
        <p>Banks and lending institutions are <strong>NOT considered to have an insurable interest</strong> in the growing crop, although they may require insurance as a condition for making an operating loan.</p>

        <div class="important">
          <strong>Important:</strong> At a minimum, the crop insurance should be enough to cover the cost of the farmer's loan or the costs to produce a growing crop.
        </div>

        <h3>Insurable Crops</h3>
        <p>Crops insured by private crop insurers vary geographically. In mid-western states, those crops most generally insured are:</p>
        <ul>
          <li>Corn</li>
          <li>Wheat</li>
          <li>Oats</li>
          <li>Sorghum</li>
          <li>Sugar beets</li>
          <li>Soybeans</li>
        </ul>

        <p>If the insured desires, insurance may also be written to cover other commercial crops such as:</p>
        <ul>
          <li>Tomatoes</li>
          <li>Potatoes</li>
          <li>Sweet corn</li>
          <li>Peas</li>
          <li>And others</li>
        </ul>

        <div class="exam-tip">
          <strong>Exam Tip:</strong> Banks and lending institutions may NOT be named insureds under a crop-hail policy because they don't have an insurable interest in the growing crop itself.
        </div>
      `
    },
    {
      title: "Policy Rates and Premium Calculation",
      content: `
        <h2>Policy Rates and Premium Calculation</h2>

        <h3>Rate Manual</h3>
        <p>The current crop rates are contained in the crop-hail rate manual provided by the insurer to the agent. The manual provides the information necessary to quote coverage and is updated each crop season.</p>

        <h3>Factors Affecting Rates</h3>
        <p>Rates can vary because:</p>
        <ul>
          <li>The <strong>frequency of hail damage</strong> may be greater in different localities</li>
          <li>Rates also may vary by <strong>type of crops</strong> because some crops (such as soybeans) are more susceptible to loss than others</li>
          <li>Rates may also be different for various types of a particular crop</li>
        </ul>

        <div class="key-concept">
          <strong>Rate Publication Basis:</strong>
          <p>Over the years, experience has shown some geographical areas to have a greater probability of hail damage than others. This is why rates are published on a <strong>state, county or township basis</strong>.</p>
        </div>

        <h3>Premium Calculation</h3>
        <p>The procedure to determine the rate for a specific crop and policy form are identical on the application because published rates are <strong>per $100 of insurance</strong>.</p>

        <div class="example">
          <h4>Calculating Premium:</h4>
          <p>To calculate the total premium by line, divide the limit of insurance by 100 and then multiply by the rate.</p>
          <p><strong>Formula:</strong> (Limit of Insurance ÷ 100) × Rate = Premium</p>
          <p>Premium amounts are rounded to the nearest dollar.</p>
        </div>

        <div class="exam-tip">
          <strong>Exam Tip:</strong> Crop-hail rates are NOT published on a national basis. They are published on a state, county, or township basis to reflect local hail damage frequency.
        </div>
      `
    },
    {
      title: "The Crop Hail Policy Structure",
      content: `
        <h2>The Crop Hail Policy Structure</h2>

        <h3>Three Main Parts of a Crop-Hail Policy</h3>
        <p>The three main parts to a crop-hail policy are:</p>
        <ol>
          <li><strong>The Application</strong></li>
          <li><strong>The General Provisions</strong></li>
          <li><strong>The Special Provisions</strong></li>
        </ol>

        <p>In addition, the policy may contain <strong>optional provisions, endorsements, and an explanation of policy terms</strong>.</p>

        <h3>The Application</h3>
        <p>The application contains the name of the insurer, the insured, and the name and location of the crop(s) to be insured.</p>

        <div class="key-concept">
          <strong>General Provisions (Policy Jacket):</strong>
          <ul>
            <li>State the insuring agreement</li>
            <li>Define the insurance period</li>
            <li>Describe the duties of both parties</li>
          </ul>
        </div>

        <h3>Special Provisions</h3>
        <p>The special provisions contain any individual state specific provisions, the perils insured against, and other provisions.</p>

        <h3>Optional Provisions</h3>
        <p>The optional provisions are used to amend the coverage through the use of a chosen policy form deductible provision. Specific endorsements may be either optional or mandatory depending on the crop or the state.</p>

        <h3>Explanation of Policy Terms</h3>
        <p>The explanation of policy terms contains terms, definitions and explanations to help understand the policy.</p>

        <div class="important">
          <strong>Producer vs. Agent:</strong>
          <p>One area of terminology that may be confusing in crop insurance is the term "producer" since federal laws refer to the individual producing the crop as an agricultural producer. On the other hand, many state insurance divisions refer to a licensed insurance agent as an insurance producer. Therefore, the term producer could be the farmer, or it could be a licensed insurance agent depending on the context.</p>
        </div>
      `
    },
    {
      title: "The Application and Binder",
      content: `
        <h2>The Application and Binder</h2>

        <h3>Application as Binder</h3>
        <p>In the crop-hail field, the application serves as a <strong>binder</strong>. When properly completed, dated, and signed, and accompanied by the premium or a promissory note, the application serves as a binder.</p>

        <h3>Application as Declarations</h3>
        <p>In the crop-hail field, the application also serves as the <strong>declarations section</strong> of the policy.</p>

        <h3>Required Signatures</h3>
        <div class="key-concept">
          <strong>For coverage to be effective:</strong>
          <p>The application must be signed by <strong>both the applicant and agent</strong>, i.e., the farmer and resident licensed insurance producer. In addition, the agent must indicate the <strong>date and actual hour</strong> that the application was signed.</p>
        </div>

        <h3>Required Information on Application</h3>
        <p>In addition to the applicant's name and address, the crop-hail application requests information on:</p>
        <ul>
          <li>The type of crop to be insured</li>
          <li>The legal description of the field by <strong>¼ section, section, township, range, and county</strong></li>
          <li>The applicant's percentage interest in the crop</li>
          <li>The number of acres to be insured</li>
          <li>The amount of insurance per acre</li>
          <li>The total amount of insurance</li>
          <li>The rate and premium</li>
        </ul>

        <h3>Landlord/Tenant Information</h3>
        <p>The applicant must provide the name of the landlord or tenant of the land on which the insured crops are to be grown, if the landlord or tenant is someone other than the insured.</p>
        <p>Both farm owners and tenant farmers may have an insurable interest in a crop, and each may insure its interest separately.</p>

        <h3>Additional Acreage</h3>
        <p>If the applicant has any additional acres of crops specified in the application but intends not to include them under the policy, the location of such crops must be given.</p>

        <h3>Additional Insurance Disclosure</h3>
        <p>The applicant must also indicate whether additional insurance from specialty crop-hail insurers has been purchased on the crops (other than FCIC insurance).</p>

        <h3>Pre-existing Damage Disclosure</h3>
        <p>The insured must also indicate whether any of the crops have been hailed upon prior to the signing of the application. One of the crop-hail policy's general provisions is that there is <strong>no coverage for crops already damaged by hail</strong> when the application is signed.</p>

        <div class="exam-tip">
          <strong>Exam Tip:</strong> The crop-hail application must designate the <strong>licensed producer who solicited and wrote the policy</strong>. It must be signed by BOTH the applicant AND the agent.
        </div>
      `
    },
    {
      title: "Company Rejection Provision",
      content: `
        <h2>Provision for Company Rejection</h2>

        <p>The application provides that the company may reject the application within <strong>240 hours</strong> of the effective date and hour of the application, upon giving <strong>five days written notice</strong> of its intent.</p>

        <div class="key-concept">
          <strong>Key Timeframes:</strong>
          <ul>
            <li>Company may reject within <strong>240 hours</strong> of effective date</li>
            <li>Must give <strong>5 days written notice</strong></li>
          </ul>
        </div>

        <div class="exam-tip">
          <strong>Exam Tip:</strong> Remember: 240 hours = 10 days. The company has up to 10 days (240 hours) to reject an application.
        </div>
      `
    },
    {
      title: "Term and Limits of Coverage",
      content: `
        <h2>Term and Limits of Coverage</h2>

        <h3>Term of Coverage</h3>
        <p>Crop Hail policies are issued for a term of <strong>one, three, or five seasons</strong>.</p>

        <p>The provisions of the Crop Hail Policy with respect to inception and termination are unique and reflect the exposure insured.</p>

        <h3>Limits of Coverage (Insurable Value)</h3>
        <p>Crop Hail insurance coverage can be purchased in any amount up to the full value of the expected crop. Coverage is a matter of individual choice.</p>

        <h3>Pre-Season Contracts</h3>
        <p>On early pre-season contracts, the coverage is usually for an average crop yield, but adjusted for the expectation of the individual farmer.</p>

        <h3>Timing of Purchase</h3>
        <p>Farmers sometimes delay purchasing insurance until they can see what the crop is like. The amount of insurance should be a high proportion of the potential value of the crop and should not be less than the cost of production.</p>

        <h3>Underwriting Considerations</h3>
        <p>Although crop-hail companies will write insurance for any legitimate amount, underwriters may request justification for extremely high amounts of coverage.</p>

        <div class="key-concept">
          <strong>Company Preferences:</strong>
          <p>Many companies prefer that policies be written for <strong>no more than 80%</strong> of the full anticipated value (that is, the yield times the expected market price) in order to allow for fluctuations in prices and yields.</p>
        </div>

        <p>Tailoring the coverage to the individual's needs or desires presents no problem, since the premium rates are proportionate to the coverage purchased.</p>

        <div class="exam-tip">
          <strong>Exam Tip:</strong> Crop-Hail policies can be issued for terms of 1, 3, or 5 seasons. Companies often prefer coverage not exceed 80% of anticipated value.
        </div>
      `
    },
    {
      title: "Policy Provisions - General Coverage",
      content: `
        <h2>Policy Provisions - National Crop Insurance Services (NCIS) General Provisions</h2>

        <h3>1. Coverage</h3>
        <p>"We cover the crops specified at the locations described in the Schedule of Insurance. We do not cover crops that have been damaged by an insured peril(s) prior to signing the application."</p>

        <h3>2. Insurance Period</h3>
        <p>The insurance is in effect from the time the crop is clearly visible above the ground, i.e., a growing crop, and until the crop is harvested, except:</p>
        <ul>
          <li>No coverage is in effect until <strong>12:01 a.m.</strong> on the day following the signing of the application by the agent and insured</li>
        </ul>

        <h3>The "Waiting Period"</h3>
        <div class="key-concept">
          <strong>Important:</strong> The policy does not take effect, however, in the event that any acre of crops described in the schedule is damaged by an insured peril during this <strong>"waiting period"</strong> between the time the application is signed and 12:01 a.m. the next day.
        </div>

        <h3>Damage During Waiting Period</h3>
        <p>If crops are damaged by hail during the waiting period, the applicant must give the insurer notice of such damage within <strong>72 hours</strong> after such damage. If notice is given in a timely fashion, the applicant is entitled to a <strong>return of premium</strong> for insurance on any such acre of crops damaged.</p>

        <p>When the applicant fails to notify the insurer within 72 hours, the right to return premium is <strong>automatically forfeited</strong>.</p>

        <div class="exam-tip">
          <strong>Exam Tip:</strong> The Crop-Hail policy takes effect at 12:01 a.m. STANDARD TIME on the day FOLLOWING the application signing. The waiting period is from signing until 12:01 a.m. the next day.
        </div>
      `
    },
    {
      title: "Duties After Loss and Loss Payment",
      content: `
        <h2>Duties After Loss and Loss Payment</h2>

        <h3>3. Duties After Loss</h3>
        <p>The company must adjust all losses and pay the loss within <strong>30 days</strong> after reaching agreement with the insured. At times the company may defer the adjustment of a covered loss until the actual loss can be determined.</p>

        <h3>4. Loss Payment</h3>
        <p>The amount payable per acre will be the limit of insurance X the percentage of loss. Payment cannot exceed the actual cash value of the destroyed crop.</p>

        <h3>5. Reduction of Insurance</h3>
        <div class="key-concept">
          <strong>Important Concept:</strong>
          <p>Unlike many forms of insurance, the crop-hail policy provides that the amount of insurance is <strong>reduced by the amount of any loss paid</strong>.</p>
          <p><strong>Example:</strong> If a crop is insured for $600 per acre and suffers a 50% loss, the remaining coverage is $300 per acre.</p>
        </div>

        <div class="exam-tip">
          <strong>Exam Tip:</strong> The insurer must pay the loss within 30 days after reaching agreement with the insured. Coverage is REDUCED by the amount of loss paid.
        </div>
      `
    },
    {
      title: "Appraisal and Other Policy Provisions",
      content: `
        <h2>Appraisal and Other Policy Provisions</h2>

        <h3>6. Appraisal</h3>
        <p>If the company and the insured fail to agree on the percentage of loss caused by an insured peril, the insured and company pay the appraiser respectively selected by them, and bear equally the expenses for the umpire.</p>

        <h3>7. Liberalization</h3>
        <p>"If we adopt any revision which would broaden the coverage under this policy without additional premium, the broadened coverage will apply."</p>

        <h3>8. Variation in Acreage in Case of Loss</h3>
        <p>When the actual acreage of any crop insured is more or less than the number of acres stated in the application and the policy, the amount of insurance per acre is revised.</p>

        <div class="example">
          <h4>Example:</h4>
          <p>The maximum amount payable per acre is calculated by dividing the face amount of the Crop Hail Policy by the number of acres insured.</p>
          <p>If the application stipulates that 200 acres are to be insured at $500 per acre, for a total of $100,000, and the actual acreage turns out to be 250, the insurance per acre is reduced to $400 per acre.</p>
          <p>The reverse would be true if the acreage were overstated, subject to the limitation that the insurance per acre cannot exceed the actual value of the crop insured.</p>
        </div>

        <h3>9. Entire Agreement, Waiver or Change of Policy Provisions</h3>
        <p>The policy, application and endorsements constitute the entire agreement between the insurer and insured. A waiver or change of any provision must be in writing and approved by the insurer.</p>

        <h3>10. Assignment of Interest</h3>
        <p>"You may not assign your interest in this policy without our written consent."</p>

        <h3>11. Assignment of Indemnity</h3>
        <p>"You may assign to another party your right to an indemnity for the crop year only on our form and with our approval. The assignee will have the right to submit the loss notices and forms required by the policy."</p>
      `
    },
    {
      title: "Additional Policy Provisions",
      content: `
        <h2>Additional Policy Provisions</h2>

        <h3>12. Concealment or Fraud</h3>
        <p>"We do not provide coverage for any insured who has intentionally concealed or misrepresented any material fact or circumstance relating to this insurance, either before or after a loss."</p>

        <h3>13. Cancellation of Policy</h3>
        <p>Generally, crop-hail policies allow the insured to cancel the policy prior to the inception of the insurance period and receive a refund of the paid premium for the amount of insurance canceled.</p>
        <p>Cancellation by the insurer requires at least <strong>20 days notice</strong> in South Dakota before the effective date and hour cancellation takes effect.</p>
        <p>In the event of "known crop failure," the policy may be returned for a prorata return of paid premium.</p>

        <h3>14. Exclusions</h3>
        <p>The standard crop-hail policies include general exclusions that are applicable regardless of the crop.</p>
        <p>In addition, there are sometimes additional exclusions in the special provisions sheet that are unique to particular crops.</p>

        <div class="key-concept">
          <strong>Important Exclusion:</strong>
          <p>Regardless of form, no insurance attaches to any acre of crop until a normal stand of crop is <strong>clearly visible above the ground</strong>.</p>
        </div>

        <h3>Standard Exclusions in Crop-Hail Policies</h3>
        <ul>
          <li>(a) Loss to any acre of crop damaged by hail before the application is signed</li>
          <li>(b) Loss by any uninsured peril whether or not in conjunction with an insured peril</li>
          <li>(c) Loss resulting from neglect or failure of the insured to harvest matured crops</li>
          <li>(d) Loss of any portion of a crop recoverable by harvesting equipment</li>
          <li>(e) Damage to the vegetative or flowering portion of a plant, tree, or shrub, except insofar as such damage results in loss to the crop itself</li>
        </ul>

        <h3>Nuclear Energy Exclusion</h3>
        <p>Crop-hail policies also contain a Nuclear Energy exclusion.</p>
      `
    },
    {
      title: "Abandonment, Suits, and Subrogation",
      content: `
        <h2>Abandonment, Suits, and Subrogation</h2>

        <h3>15. Abandonment of Crop</h3>
        <p>"We will not accept abandonment to us of any interest in any crop."</p>

        <h3>16. Suit Against Us</h3>
        <p>"You cannot bring suit or action against us unless you have complied with all of the policy provisions."</p>

        <h3>17. Conformity to Statutes</h3>
        <p>"If any terms of this policy are in conflict with statutes of the state in which this policy is issued, the policy will conform to such statutes."</p>

        <h3>18. Subrogation (Recovery of Loss From a Third Party)</h3>
        <div class="key-concept">
          <strong>Subrogation Provision:</strong>
          <p>"Because you will be able to recover all or a part of your loss from someone other than us, you must do all you can to preserve any such rights. If we pay you for your loss then your right of recovery will belong to us. If we recover more than we paid you plus our expenses, the excess will be paid to you."</p>
        </div>

        <h3>19. Inspections</h3>
        <p>"We will be permitted, but not obligated, to inspect your property and operations at any time."</p>
        <p>(The inspections do not constitute a warranty to the insured that the property or operations are safe or healthful, or are in compliance with any law, rule or regulation).</p>

        <h3>20. Pre-Judgment Interest</h3>
        <p>"If you and we do not reach agreement on the amount of loss, and whether we determine the amount of loss through the appraisal process or you file suit against us, you are not entitled to pre-judgment interest."</p>

        <div class="exam-tip">
          <strong>Exam Tip:</strong> The Crop Hail provision that grants the insurance company the right of recovery of loss from a third party is called <strong>Subrogation</strong>.
        </div>
      `
    },
    {
      title: "NCIS Special Provisions - Perils Insured",
      content: `
        <h2>NCIS Special Provisions</h2>

        <h3>1. Perils Insured Against</h3>
        <p>The Crop Hail Policy typically terminates when the crop is harvested with the exception of <strong>Fire and Transit coverage</strong>.</p>

        <div class="key-concept">
          <strong>Perils Covered Under Standard Policy:</strong>
          <ul>
            <li><strong>Hail</strong></li>
            <li><strong>Fire</strong></li>
            <li><strong>Lightning</strong></li>
          </ul>
        </div>

        <p>Coverage against fire and lightning applies before the crop is harvested and while it is still in the harvester.</p>

        <h3>Fire Coverage Details</h3>
        <p>Coverage applies to loss by fire to any insured crop while outside of buildings and until unloaded at the first place of storage, whether temporary or permanent.</p>
        <p><strong>First place of storage</strong> means any drying apparatus, drying bins or storage facility of any kind.</p>

        <h3>Transit Coverage</h3>
        <p>While the harvested crop is being transported to the first place of storage <strong>not to exceed 100 miles</strong> (previously 50 miles), the crop is insured against loss because of fire, lightning, windstorm, collision, or overturn of the transporting vehicle and collapse of bridges, docks, or culverts.</p>
        <p>Transit coverage is <strong>excess over any other valid and collectible insurance</strong>.</p>

        <h3>Fire Department Service Charge</h3>
        <p>"We will pay up to <strong>$500</strong> (previously $250) for your obligation assumed by contract or agreement for fire department charges incurred when the fire department is called to save or protect the unharvested crop."</p>

        <h3>No Excess Over Loss or Deductible</h3>
        <p>"No Excess Over Loss or Deductible will apply to Fire, Lightning, Transit Coverage or Fire Department Service Charge."</p>

        <div class="exam-tip">
          <strong>Exam Tip:</strong> The standard Crop-Hail policy does NOT cover wind damage (except in transit). It covers Hail, Fire, and Lightning. The fire department service charge limit is $500.
        </div>
      `
    },
    {
      title: "Catastrophe Loss Award",
      content: `
        <h2>2. Catastrophe Loss Award</h2>

        <div class="key-concept">
          <strong>Catastrophe Loss Award:</strong>
          <p>"When a loss exceeds 70% on any acre of the insured crop, an additional amount of <strong>one-half of the percent of loss that is in excess of 70%</strong> will be paid."</p>
        </div>

        <h3>Limitations</h3>
        <ul>
          <li>The total amount payable per acre will not exceed the amount of insurance applying at the time of loss</li>
          <li>This award will not be paid if the loss is subject to any Excess Over Loss or Deductible provision which does not disappear at or less than 70% loss</li>
        </ul>

        <div class="example">
          <h4>Example Calculation:</h4>
          <p>If loss is 80%, the excess over 70% is 10%.</p>
          <p>Half of 10% = 5% additional payment</p>
          <p>Total payment = 80% + 5% = 85% of coverage</p>
        </div>

        <div class="exam-tip">
          <strong>Exam Tip:</strong> The Catastrophe Loss Award kicks in when loss exceeds 70%. You receive an additional half of the percentage above 70%.
        </div>
      `
    },
    {
      title: "Special Crop Provisions",
      content: `
        <h2>Special Crop Provisions</h2>

        <h3>3. Canning Beans and Canning Peas</h3>
        <p>"Insurance on canning beans and canning peas will expire <strong>60 days</strong> after the crop is clearly visible above the ground."</p>

        <h3>4. Corn and Sorghum Seed Production</h3>
        <p>On corn or sorghum crops grown for seed purposes, the amount of any loss will be determined in the same manner as for ordinary field corn and ordinary field sorghum.</p>

        <h3>5. Hay, Forage and Grass Crops</h3>
        <p>"For hay, forage or other crops harvested more than once each growing season, the limit of insurance per acre provided for each cutting or harvest will be determined by dividing the total insurance per acre by the number of cuttings or harvests."</p>

        <div class="example">
          <h4>Example:</h4>
          <p>If three "cuttings" of hay are anticipated from a particular acreage, the amount of coverage on that acreage is reduced by 1/3 after the first cutting and by an additional 1/3 after each subsequent cutting.</p>
        </div>

        <p>The amount of insurance is reduced in the same proportion as each acre of the crop is cut, dug, picked, pulled, or stripped.</p>

        <h3>6. Replanting Destroyed Crops</h3>
        <p>The crop-hail policy also includes a special provision relating to the replanting of crops, which is designed to reduce both the insured's loss and the insurer's losses.</p>
        <p>When crops are damaged to the extent that replanting is necessary, and when such replanting is possible, the insurer agrees to reimburse the insured for the actual cost of replanting the crop, <strong>up to 20%</strong> of the insurance applicable to the crop.</p>
        <p>This reimbursement does not reduce the amount of insurance applicable to the crop, and once the replanted crop shows a normal stand, it is covered up to the full amount of the insurance.</p>
        <p>You have up to <strong>25 days</strong> to replant.</p>

        <div class="exam-tip">
          <strong>Exam Tip:</strong> The replanting provision reimburses up to 20% of insurance for replanting costs. You have 25 days to replant.
        </div>
      `
    },
    {
      title: "Multiple-Term Policies and Cancellation",
      content: `
        <h2>Multiple-Term Policies and Cancellation</h2>

        <h3>7. Multiple-Term Policies</h3>
        <p>"If you have selected the multiple-year term option, your policy will be in effect for the consecutive growing seasons of the term selected from the date of signing the original application."</p>

        <h3>8. Cancellation</h3>
        <p>"You may cancel this policy at any time by giving us written notice and returning the policy to us. If you cancel, we will refund premium you have paid for the current season, if any, according to the table."</p>

        <h3>9. Expiration of Insurance</h3>
        <p>Insurance expires at <strong>12:01 a.m. standard time</strong> on a specified date determined by the crop insured and the state. The specific expiration dates are printed in the form and coincide with the particular harvest period of the crop.</p>

        <div class="key-concept">
          <strong>Key Dates:</strong>
          <p>Actual expiration dates are set on the basis of typical growth and harvest patterns of individual crops, reflecting the climate of the state (or county) in which the crop is located. The expiration date of the policy depends on the crop, and specific expiration dates are scheduled in the policy.</p>
        </div>

        <p>Because the dates are subject to change, the policy should be checked from year to year concerning:</p>
        <ul>
          <li>The closing dates for sales</li>
          <li>Closing dates for changing coverage</li>
          <li>The last planting date for which provision is made</li>
        </ul>

        <h3>Purpose of Different Expiration Dates</h3>
        <p>The purpose of scheduling different expiration dates for different crops is to eliminate coverage from crops that have been left out long past the time for harvest and have, therefore, been subject to loss exposures not anticipated in the rates.</p>

        <div class="exam-tip">
          <strong>Exam Tip:</strong> Crop Hail Policy expires at 12:01 a.m. STANDARD TIME on the specified date.
        </div>
      `
    },
    {
      title: "Optional Provisions - Deductibles",
      content: `
        <h2>Optional Provisions - Deductibles</h2>

        <p>The various deductibles are called "optional provisions."</p>

        <p>Crop-hail policies are generally written subject to a deductible, which is designed to eliminate small losses which could cost more to adjust than the amount of the loss.</p>

        <h3>Excess of Loss Clauses</h3>
        <p>Crop-hail policy deductibles are generally referred to as <strong>"excess of loss" clauses</strong> (optional provisions), and apply as a percentage of the value insured, rather than as a fixed dollar amount.</p>

        <p>Several deductible options are available including the <strong>Flat or Straight Deductible</strong>.</p>

        <div class="key-concept">
          <strong>Types of Deductibles:</strong>
          <ul>
            <li><strong>Flat/Straight Deductible:</strong> Fixed percentage that applies to all losses</li>
            <li><strong>Disappearing Deductible:</strong> Deductible reduces as loss increases</li>
          </ul>
        </div>

        <div class="exam-tip">
          <strong>Exam Tip:</strong> The "excess of loss" clause in the Crop Hail optional provisions refers to the <strong>Deductible</strong>.
        </div>
      `
    },
    {
      title: "Liability and Claim Settlement",
      content: `
        <h2>Liability and Claim Settlement Practices</h2>

        <h3>Pro Rata Liability Clause</h3>
        <p>The crop-hail policy includes a <strong>pro-rata liability clause</strong> which provides that the insurer is not liable for a greater proportion of any loss than the amount of insurance under this policy bears to the total amount of insurance covering the insured's interest in the crop, whether collectible or not.</p>

        <p>However, the pro-rata clause does not apply to Federal Crop Insurance Corporation policies. Policies issued through private insurers do not pro-rate with FCIC policies, i.e., you can collect from both.</p>

        <h3>1. Claim Site Assessment</h3>
        <p>The percentage of loss will be determined using industry approved methods and adjustment procedures for crop-hail losses.</p>

        <h3>2. Notice of Loss</h3>
        <p>The insured must give notice of loss to the company within some number of days or time frame specified in the policy.</p>

        <div class="important">
          <strong>Important:</strong> In the event of a probable loss due to hail, the claim must be reported to the company within <strong>10 calendar days</strong> of the occurrence.
        </div>

        <h3>3. Insured's Duties After Loss</h3>
        <p>The insured must report the loss within the time frame given in the policy.</p>
        <p>The insured must continue to care for each damaged field until the adjuster has examined the field.</p>

        <h3>4. Percentage Plan</h3>
        <p>Under the percentage plan policy, the company permits the insured to place a valuation on the crop, which is then the amount of insurance purchased. In the event of loss, the indemnity is based on the percentage of the crop damaged by hail, fire, or lightning.</p>

        <div class="example">
          <h4>Example:</h4>
          <p>If the insured wishes to purchase coverage in the amount of $100 per acre on 160 acres, the amount of coverage would be $16,000. If the hail damage is certified to be 50%, the indemnity payable would be $8,000, regardless of the value of the half-crop eventually harvested.</p>
        </div>

        <p>Indemnities are based on the amount of insurance purchased and the percentage of crop loss.</p>
      `
    },
    {
      title: "Appraisal and Arbitration",
      content: `
        <h2>Appraisal and Arbitration</h2>

        <h3>5. Appraisal</h3>
        <p>The insured and company pay the appraiser respectively selected by them, and bear equally the expenses for the umpire when the percentage of loss (amount to be paid) is in disagreement.</p>

        <h3>Damage Assessment</h3>
        <p>Damage is measured in terms of stand reduction or percentage of grain heads or corn ears lost and so on. The damage is assessed by the insurance company adjuster as soon as possible after the event, so that the insured damage can be isolated from other causes.</p>

        <h3>Indemnity Calculation</h3>
        <p>The indemnity payable is computed on the basis of the percentage damage, as determined by the adjuster, times the amount of coverage in the contract, regardless of the potential or actual yield of the crop.</p>

        <h3>Arbitration</h3>
        <div class="key-concept">
          <strong>Dispute Resolution:</strong>
          <p>Any failure by the insured and company to agree on issues other than the percentage of loss are settled by <strong>arbitration</strong>.</p>
        </div>

        <h3>6. Loss Payment</h3>
        <p>At the date of loss, the limit of insurance is multiplied by the percentage of loss. This equals the amount payable per acre. The amount payable may not exceed the actual cash value of the portion of the crop destroyed.</p>

        <div class="exam-tip">
          <strong>Exam Tip:</strong> When the insured and insurer cannot agree on the amount to be paid for a crop insurance loss, the claim is settled by <strong>Arbitration</strong>.
        </div>
      `
    }
  ],
  quizQuestions: [
    {
      question: "A crop-hail policy covers which of the following perils?",
      options: [
        "Wind",
        "Fire",
        "Rain",
        "Flood"
      ],
      correct: 1,
      explanation: "The standard crop-hail policy covers hail, fire, and lightning. It does NOT cover wind (except during transit), rain, or flood."
    },
    {
      question: "The perils covered under the standard crop-hail policy do NOT include:",
      options: [
        "Hail",
        "Fire",
        "Rain",
        "Lightning"
      ],
      correct: 2,
      explanation: "Rain is NOT covered under the standard crop-hail policy. The covered perils are hail, fire, and lightning."
    },
    {
      question: "In order to purchase crop-hail insurance, a person must have:",
      options: [
        "A farm credit loan",
        "At least 3 years production history",
        "An insurable interest in the growing crop",
        "Participated in a government farm program"
      ],
      correct: 2,
      explanation: "To be eligible for crop insurance, a person must have an insurable interest (economic stake) in the growing crop."
    },
    {
      question: "Which of the following may NOT be an insured under a crop-hail policy?",
      options: [
        "Landowner",
        "Sharecropper",
        "Tenant farmer",
        "Bank or lending institution"
      ],
      correct: 3,
      explanation: "Banks and lending institutions are NOT considered to have an insurable interest in the growing crop, although they may require insurance as a condition for loans."
    },
    {
      question: "Crop-hail rates are NOT published on a:",
      options: [
        "State basis",
        "County basis",
        "National basis",
        "Township basis"
      ],
      correct: 2,
      explanation: "Crop-hail rates are published on a state, county, or township basis - NOT national. This reflects varying hail damage frequency in different localities."
    },
    {
      question: "The crop hail application must designate the:",
      options: [
        "Lessor",
        "Lender",
        "Producer's gross profit",
        "Licensed producer who solicited and wrote the policy"
      ],
      correct: 3,
      explanation: "The application must designate the licensed producer (agent) who solicited and wrote the policy, along with the applicant's signature."
    },
    {
      question: "The crop-hail policy must be signed by the:",
      options: [
        "Tenant",
        "Landowner",
        "Producer and insured",
        "Named insured"
      ],
      correct: 2,
      explanation: "The crop-hail policy must be signed by BOTH the applicant (farmer) and the agent (licensed insurance producer)."
    },
    {
      question: "The crop-hail policy does NOT request information on the location of the crop by:",
      options: [
        "Range",
        "Community",
        "Section",
        "County"
      ],
      correct: 1,
      explanation: "The application requests legal description by ¼ section, section, township, range, and county - NOT by community."
    },
    {
      question: "Which of the following questions is NOT asked on a crop hail application?",
      options: [
        "Has there been any hail damage in previous years?",
        "Does the crop currently have hail damage prior to the signing of the application?",
        "What percentage is the crop to be insured?",
        "Is there additional insurance purchased on the crops?"
      ],
      correct: 0,
      explanation: "The application asks about current hail damage (not previous years), percentage to be insured, and additional insurance - but NOT about damage in previous years."
    },
    {
      question: "The crop hail application may be rejected by the company within how many hours of the effective date and hour?",
      options: [
        "30 hours",
        "60 hours",
        "120 hours",
        "240 hours"
      ],
      correct: 3,
      explanation: "The company may reject the application within 240 hours (10 days) of the effective date, upon giving five days written notice."
    },
    {
      question: "Crop-Hail Policies are issued for a term of:",
      options: [
        "1 season",
        "3 seasons",
        "5 seasons",
        "1, 3, or 5 seasons"
      ],
      correct: 3,
      explanation: "Crop Hail policies can be issued for a term of one, three, or five seasons."
    },
    {
      question: "The Crop-Hail Policy takes effect at the time the crop is:",
      options: [
        "Sold",
        "Planted",
        "Harvested",
        "Clearly visible above the ground"
      ],
      correct: 3,
      explanation: "The insurance is in effect from the time the crop is clearly visible above the ground (a growing crop) until harvested."
    },
    {
      question: "The Crop-Hail insurer must pay the loss within how many days after reaching agreement with the insured?",
      options: [
        "30 days",
        "45 days",
        "60 days",
        "90 days"
      ],
      correct: 0,
      explanation: "The company must adjust all losses and pay the loss within 30 days after reaching agreement with the insured."
    },
    {
      question: "The Crop-Hail Policy permits the company to defer the adjustment of a covered loss until:",
      options: [
        "The crop is fully harvested",
        "The end of the policy period",
        "The actual loss can be determined",
        "The company has adequate cash flow"
      ],
      correct: 2,
      explanation: "The company may defer the adjustment of a covered loss until the actual loss can be determined."
    },
    {
      question: "When a loss is paid under a Crop Hail Policy, the amount of insurance available for future claims is:",
      options: [
        "Reduced to zero",
        "Reduced by the amount paid",
        "Unaffected by the amount paid",
        "Reduced by the amount of the deductible"
      ],
      correct: 1,
      explanation: "Unlike many forms of insurance, the crop-hail policy provides that the amount of insurance is reduced by the amount of any loss paid."
    },
    {
      question: "In the event the insured and insurer cannot agree on the amount to be paid for a crop insurance loss, the claim is settled by:",
      options: [
        "Appraisal",
        "Arbitration",
        "Subrogation",
        "Legal action"
      ],
      correct: 0,
      explanation: "When the insured and insurer cannot agree on the percentage of loss (amount to be paid), the claim is settled through the Appraisal process."
    },
    {
      question: "NCIS general Crop Hail provisions allow the insured to make valid assignment with:",
      options: [
        "Verbal notification to the agent",
        "Written consent of the company",
        "Either verbal or written notice to the company",
        "Endorsement"
      ],
      correct: 1,
      explanation: "You may not assign your interest in the policy without the company's written consent."
    },
    {
      question: "The NCIS general Crop Hail provisions permits assignment of indemnity:",
      options: [
        "Without the insurer's approval",
        "Upon cancellation of the crop policy",
        "Only on the insurer's form and with approval",
        "But the assignee does not have the right to submit loss notices"
      ],
      correct: 2,
      explanation: "Assignment of indemnity must be on the insurer's form and with their approval. The assignee will have the right to submit loss notices."
    },
    {
      question: "Intentional concealment or misrepresentation of any material fact by the insured will:",
      options: [
        "Result in a pro-rata loss settlement",
        "Invalidate the Crop-Hail Policy",
        "Have no effect on policy coverage",
        "Reduce the coverage under the policy"
      ],
      correct: 1,
      explanation: "The policy provides no coverage for any insured who has intentionally concealed or misrepresented any material fact."
    },
    {
      question: "A crop hail policy would cover:",
      options: [
        "Fire and lightning damage while the crop is still in the harvester",
        "Damage to the vegetative or flowering portion of a plant, tree, or shrub",
        "Loss to any acre of crop damaged by hail before the application is signed",
        "Loss resulting from neglect or failure of the insured to harvest matured crops"
      ],
      correct: 0,
      explanation: "Coverage against fire and lightning applies before the crop is harvested and while it is still in the harvester."
    },
    {
      question: "The Crop Hail provision that grants the insurance company the right of recovery of loss from a third party is:",
      options: [
        "Suit Against Us",
        "Subrogation",
        "Conformity to Statues",
        "Assignment of Indemnity"
      ],
      correct: 1,
      explanation: "Subrogation allows the insurer to recover losses from a responsible third party after paying a claim to the insured."
    },
    {
      question: "The standard Crop-Hail policy does NOT cover:",
      options: [
        "Hail",
        "Fire",
        "Wind",
        "Lightning"
      ],
      correct: 2,
      explanation: "The standard Crop-Hail policy covers hail, fire, and lightning - but does NOT cover wind (except during transit)."
    },
    {
      question: "Crop-Hail coverage against fire and lightning applies:",
      options: [
        "To the crop in any place of storage",
        "Only to the harvested crop in transit",
        "Only in the field before the crop is harvested",
        "Before the crop is harvested and while it is still in the harvester"
      ],
      correct: 3,
      explanation: "Fire and lightning coverage applies before harvest and while in the harvester, and continues until the crop reaches first place of storage."
    },
    {
      question: "The transit extension of the Crop-Hail policy does NOT cover against loss that occurs:",
      options: [
        "More than 100 miles from the harvester",
        "By overturn of the transporting vehicle",
        "By collapse of bridges",
        "Because of fire, lightning, or wind"
      ],
      correct: 0,
      explanation: "Transit coverage applies only within 100 miles of the harvester. It covers fire, lightning, windstorm, collision, overturn, and collapse of bridges."
    },
    {
      question: "The limit of payment under Crop-Hail policies for fire department service charge coverage is:",
      options: [
        "$500",
        "$750",
        "$1,000",
        "$1,500"
      ],
      correct: 0,
      explanation: "Fire department service charge coverage pays up to $500 for fire department charges incurred to save or protect the unharvested crop."
    },
    {
      question: "Under a Crop Hail Policy, when crops are damaged to the extent that replanting is necessary, and when such replanting is possible, the insurer agrees to reimburse the insured for the actual cost of replanting the crop, up to:",
      options: [
        "10% of the insurance applicable to the crop",
        "20% of the insurance applicable to the crop",
        "65% of the insurance applicable to the crop",
        "80% of the insurance applicable to the crop"
      ],
      correct: 1,
      explanation: "The insurer agrees to reimburse the insured for replanting costs up to 20% of the insurance applicable to the crop."
    },
    {
      question: "Under the Crop Hail Policy replant provision, the crop must be replanted within:",
      options: [
        "10 days",
        "15 days",
        "20 days",
        "25 days"
      ],
      correct: 3,
      explanation: "Under the replant provision, you have up to 25 days to replant."
    },
    {
      question: "The Crop Hail Policy permits cancellation by the insured:",
      options: [
        "Only after a loss",
        "Only prior to a loss",
        "Upon written notice to the insurer",
        "Upon verbal notice to the insurance agent"
      ],
      correct: 2,
      explanation: "You may cancel the policy at any time by giving written notice and returning the policy to the insurer."
    },
    {
      question: "The Crop Hail Policy expires at:",
      options: [
        "12:01 a.m. central time",
        "12:01 a.m. eastern time",
        "12:01 a.m. standard time",
        "12:01 a.m. mountain time"
      ],
      correct: 2,
      explanation: "Insurance expires at 12:01 a.m. STANDARD TIME on a specified date determined by the crop insured and the state."
    },
    {
      question: "The 'excess of loss' clause in the Crop Hail optional provisions refers to the:",
      options: [
        "Deductible",
        "Loss payment",
        "Catastrophe loss award",
        "Reduction of insurance"
      ],
      correct: 0,
      explanation: "Crop-hail policy deductibles are generally referred to as 'excess of loss' clauses and apply as a percentage of value insured."
    }
  ]
};

export default chapter2;
